import { PlexRoute } from '@all-hands-hq/plex-graphql-schema/dist/src';
import { NetworkStatus } from '@apollo/client';
import { CardActionArea, makeStyles, Paper } from '@material-ui/core';
import MilestoneIcon from '@material-ui/icons/Flag';
import assertNever from 'assert-never';
import classNames from 'classnames';
import Moment from 'moment-timezone';
import React, { useCallback, useMemo, useRef } from 'react';

import { ReactComponent as TimelineEventSvg } from '../../../assets/timeline_event.svg';
import { ReactComponent as TimelinePostSvg } from '../../../assets/timeline_post.svg';
import { PostType, useGetTimelineQuery } from '../../../graphql/gen/types';
import { usePublishStatusFilter } from '../../../models/filters/usePublishStatusFilter';
import { useSelectedLabelIds } from '../../../models/filters/useSelectedLabelIds';
import { useSelectedPlexIds } from '../../../models/filters/useSelectedPlexIds';
import { bottomBorderStyle, colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { getPublishStatuses } from '../../../utils/permissions';
import { LinesEllipsis } from '../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { FilterPicker } from '../common/FilterPicker';
import { plainTextPreview } from '../common/plainTextPreview';
import { PlexName } from '../common/PlexName';
import { PostVisibility } from '../common/PostVisibility';
import { useSelectedPlexPost } from '../details/post/useSelectedPlexPost';
import { usePlexRoute } from '../details/usePlexRoute';
import { CardLabels } from '../feed/CardLabels';
import { FooterLoader } from '../feed/FooterLoader';
import { FetchMoreCallback, useInfiniteScroll } from '../feed/useInfiniteScroll';
import { useSubscribeToMorePosts } from '../feed/useSubscribeToMorePosts';
import { NewPostButton } from '../post_form/NewPostButton';
import { AnalyticsLocation, useLogAnalytics } from '../shared/logAnalytics';
import { getItemTypeSelection, ItemTypeSelection, ItemTypeSelectionEnum } from './ItemTypeSelection';
import { TimelineGraph } from './TimelineGraph';
import { TimelineHeader } from './TimelineHeader';
import { getTimeRange, TimeRangeEnum } from './TimeRange';

const getFiltersFromUrl = (
  plexRoute: PlexRoute | null
): {
  start: Moment.Moment;
  finish: Moment.Moment;
  selectedTimeOption: TimeRangeEnum;
  itemTypeSelectionEnum: ItemTypeSelectionEnum;
  itemTypeSelection: ItemTypeSelection;
} => {
  const { selectedTimeOption, start, finish } = ((): {
    start: Moment.Moment;
    finish: Moment.Moment;
    selectedTimeOption: TimeRangeEnum;
  } => {
    switch (plexRoute?.timelineFilters?.timeInterval?.type) {
      case 'this_month':
        return {
          selectedTimeOption: TimeRangeEnum.ThisMonth,
          ...getTimeRange(TimeRangeEnum.ThisMonth),
        };
      case 'custom':
        return {
          selectedTimeOption: TimeRangeEnum.Custom,
          start: Moment(plexRoute.timelineFilters.timeInterval.start),
          finish: Moment(plexRoute.timelineFilters.timeInterval.finish),
        };
      default:
        return {
          selectedTimeOption: TimeRangeEnum.ThisWeek,
          ...getTimeRange(TimeRangeEnum.ThisWeek),
        };
    }
  })();

  const itemTypeSelectionEnum = ((): ItemTypeSelectionEnum => {
    if (plexRoute?.timelineFilters?.itemType == null) {
      return ItemTypeSelectionEnum.All;
    }

    switch (plexRoute.timelineFilters.itemType) {
      case 'posts':
        return ItemTypeSelectionEnum.Post;
      case 'events':
        return ItemTypeSelectionEnum.Event;
      case 'milestones':
        return ItemTypeSelectionEnum.Milestone;
      default:
        assertNever(plexRoute?.timelineFilters?.itemType);
    }
  })();

  return {
    start,
    finish,
    selectedTimeOption,
    itemTypeSelectionEnum,
    itemTypeSelection: getItemTypeSelection(itemTypeSelectionEnum),
  };
};

export const TimelineView = (): React.ReactElement | null => {
  useLogAnalytics({ name: 'loaded page', details: { location: AnalyticsLocation.TIMELINE } });
  const classes = useStyles();
  const plexIds = useSelectedPlexIds();
  const filterPublishStatus = usePublishStatusFilter();
  const publishStatuses = useMemo(() => getPublishStatuses(filterPublishStatus), [filterPublishStatus]);
  const labelIds = useSelectedLabelIds();
  const [, setSelectedPlexPost] = useSelectedPlexPost();

  const [plexRoute, updatePlexRoute] = usePlexRoute();
  const { start, finish, selectedTimeOption, itemTypeSelectionEnum, itemTypeSelection } = React.useMemo(
    () => getFiltersFromUrl(plexRoute),
    [plexRoute]
  );

  const updateTimelineFilters = (filters: Partial<PlexRoute['timelineFilters']>): void => {
    updatePlexRoute({ timelineFilters: { ...plexRoute?.timelineFilters, ...filters } });
  };

  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const variables = React.useMemo(
    () => ({
      plexIds,
      publishStatuses,
      types: itemTypeSelection.types,
      labelIds,
      before: finish.toISOString(),
      onOrAfter: start.toISOString(),
    }),
    [finish, itemTypeSelection.types, labelIds, plexIds, publishStatuses, start]
  );
  const { data, loading: initialLoading, fetchMore, networkStatus, subscribeToMore } = useGetTimelineQuery({
    variables,
  });
  useSubscribeToMorePosts(subscribeToMore, variables);

  const startDate = start.toDate();
  const finishDate = finish.toDate();
  const posts =
    networkStatus === NetworkStatus.setVariables
      ? []
      : (data?.posts?.items || []).filter((post) => {
          const eventDate = new Date(post.eventDate);
          return !post.deleted && startDate <= eventDate && eventDate < finishDate;
        });
  const nextCursor = data?.posts?.nextCursor;
  const onFetchMore: FetchMoreCallback = useCallback(
    (isCancelled) =>
      fetchMore({
        variables: { ...variables, cursor: nextCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.posts || isCancelled()) return prev;
          return {
            ...prev,
            posts: {
              ...prev.posts,
              items: [...prev.posts.items, ...fetchMoreResult.posts.items],
              nextCursor: fetchMoreResult.posts.nextCursor,
            },
          };
        },
      }),
    [fetchMore, nextCursor, variables]
  );

  const infiniteScrollLoading = useInfiniteScroll({
    containerRef: timelineContainerRef,
    onFetchMore,
    canFetchMore: !initialLoading && nextCursor != null,
  });
  const loading = initialLoading || infiniteScrollLoading;

  return (
    <>
      <div className={classes.header}>
        <FilterPicker />
        <NewPostButton type={'event'} className={classes.createBtn} />
      </div>
      <div className={classes.root} ref={timelineContainerRef}>
        <Paper className={classes.paper}>
          <TimelineHeader
            start={start}
            finish={finish}
            selectedTimeOption={selectedTimeOption}
            updateTimelineFilters={updateTimelineFilters}
            itemTypeSelectionEnum={itemTypeSelectionEnum}
          />
          <TimelineGraph posts={posts} start={start} finish={finish} />
          <div className={classes.columnsRow}>
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnType)}
              text={'TYPE'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnDescription)}
              style={{ flex: 1 }}
              text={'DESCRIPTION'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnPlex)}
              text={'PLEX'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnVisibility)}
              text={'VISIBILITY'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnDate)}
              text={'DATE'}
              size={TextSizesEnum.XSmall}
            />
          </div>
          {posts.map((p) => (
            <CardActionArea
              key={p.id}
              id={`timeline-${p.id}`}
              component="div"
              className={classes.eventRoot}
              onClick={(): void => {
                setSelectedPlexPost({ permalink: p.permalink, commentsVisible: false });
              }}
            >
              <div className={classNames(classes.columnType)}>
                {((): React.ReactElement => {
                  switch (p.type) {
                    case PostType.Post:
                      return <TimelinePostSvg />;
                    case PostType.Event:
                      return <TimelineEventSvg />;
                    case PostType.Milestone:
                      return <MilestoneIcon />;
                    case PostType.Task:
                      throw new Error(`Task cannot be displayed on timeline`);
                  }
                })()}
              </div>
              <div
                className={classNames(classes.columnDescription)}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <div className={classes.titleContainer}>
                  <PlexTypography className={classes.title} text={p.title} size={TextSizesEnum.Normal} />
                  <CardLabels labels={p.labels} />
                </div>
                <PlexTypography className={classes.eventText} size={TextSizesEnum.XSmall}>
                  <LinesEllipsis text={plainTextPreview(p.body, p.mentions, p.links)} maxLine={2} />
                </PlexTypography>
              </div>
              <div className={classNames(classes.columnPlex)}>
                <PlexName className={classes.eventPlexName} name={p.plex.name} color={p.plex.color} />
              </div>
              <div className={classNames(classes.columnVisibility, classes.eventPublishStatus)}>
                <PostVisibility plexPublishStatus={p.publishStatus} plexName={p.plex.name} />
              </div>
              <div className={classNames(classes.columnDate)}>
                <PlexTypography
                  className={classNames(classes.eventDate)}
                  text={new Date(p.eventDate).toLocaleTimeString()}
                  size={TextSizesEnum.Normal}
                />
                <PlexTypography
                  className={classNames(classes.eventDate)}
                  text={new Date(p.eventDate).toDateString()}
                  size={TextSizesEnum.Normal}
                />
              </div>
            </CardActionArea>
          ))}

          <FooterLoader isLoading={loading} />
        </Paper>
      </div>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'auto',
    height: '100%',
    scrollBehavior: 'smooth',
    position: 'relative',
  },
  paper: {
    margin: 16,
    marginTop: 0,
    borderRadius: 6,
    paddingTop: 6,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    padding: spacings.normal,
    marginBottom: spacings.small,
    justifyContent: 'space-between',
    flexShrink: 0,
    backgroundColor: colorPalette.general.feedBackground,
    alignItems: 'center',
  },
  createBtn: {
    flexShrink: 0,
  },

  columnsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    height: 32,
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: 14, // change to account for action buttons in the future
    ...bottomBorderStyle,
  },
  columnTitle: {
    display: 'flex',
    color: '#464663',
    fontWeight: 'bold',
  },
  columnType: {
    display: 'flex',
    width: 60,
    marginLeft: 10,
  },
  titleContainer: {
    display: 'inline-block',
    lineHeight: '26px',
  },
  columnDescription: {
    display: 'flex',
    flex: 1,
  },
  columnPlex: {
    display: 'flex',
    width: 160,
    marginRight: 16,
  },
  columnVisibility: {
    display: 'flex',
    width: 60,
  },
  columnDate: {
    display: 'flex',
    width: 160,
    flexDirection: 'column',
  },
  eventRoot: {
    display: 'flex',
    flexDirection: 'row',
    padding: 14,
    ...bottomBorderStyle,
    boxSizing: 'border-box',
    '&:target': { backgroundColor: '#fafafa', animation: '$fade 2s linear' },
  },
  '@keyframes fade': {
    from: { backgroundColor: '#ddd' },
    to: { backgroundColor: '#fafafa' },
  },
  title: {
    display: 'inline',
    color: colorPalette.primaryColorBlue._500,
  },
  eventText: {
    color: colorPalette.purpleGray._500,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    marginTop: spacings.small,
  },
  eventPlexName: {
    maxWidth: 160,
    height: 'auto',
  },
  eventPublishStatus: {
    display: 'flex',
    color: colorPalette.purpleGray._500,
    fontSize: theme.typography.fontSize,
  },
  eventDate: {
    color: colorPalette.purpleGray._400,
  },
}));
